// ============================================================
// PATCHISTRY EMAIL POPUP — captures the 95% who don't open Sage
// Three variants (Iris's brand-voice copy):
//   - default: Save-Your-Build (Variant 2)
//   - Pinterest source: Pinterest-Bridge (Variant 4)
//   - Bach pages: Bach-Proof (Variant 3)
// Triggers: exit-intent (desktop), 30s timer (mobile).
// Submits to /api/concierge/capture-email — same Klaviyo path as Sage.
// ============================================================

(function () {
  'use strict';

  const ENDPOINT = 'https://patchistry-api.patchistry.workers.dev/api/concierge/capture-email';
  const STORAGE_KEY = 'pp_popup_state_v1';
  const MOBILE_TIMER_MS = 30000;

  const root = document.getElementById('pp-popup');
  if (!root) return;

  // Suppress on cart, checkout, account (intent already established)
  if (/^\/(cart|checkouts?|account)/.test(location.pathname)) return;

  // Suppress if user already submitted OR dismissed in last 7 days
  const state = loadState();
  if (state.submitted) return;
  if (state.dismissedAt && Date.now() - state.dismissedAt < 7 * 86400 * 1000) return;
  // Suppress if user has interacted with the Sage concierge (they're already in the funnel)
  try {
    const sageState = JSON.parse(localStorage.getItem('pp_conc_state_v1') || '{}');
    if (sageState.hasInteracted) return;
  } catch (e) {}

  // Pick variant based on referrer + URL
  const variant = pickVariant();
  const cfg = VARIANTS[variant];

  // Render content
  const cardEl = root.querySelector('.pp-popup-content');
  cardEl.innerHTML = `
    <p class="pp-popup-eyebrow">${escapeHtml(cfg.eyebrow)}</p>
    <h2 class="pp-popup-headline">${escapeHtml(cfg.headline)}</h2>
    <p class="pp-popup-subhead">${escapeHtml(cfg.subhead)}</p>
    <p class="pp-popup-body">${escapeHtml(cfg.body)}</p>
    <form class="pp-popup-form">
      <input
        type="email"
        class="pp-popup-input"
        placeholder="${escapeHtml(cfg.placeholder)}"
        required
        autocomplete="email"
        aria-label="Email">
      <button type="submit" class="pp-popup-cta">${escapeHtml(cfg.cta)}</button>
      <button type="button" class="pp-popup-dismiss">${escapeHtml(cfg.dismiss)}</button>
    </form>
  `;

  // Wire up events
  root.querySelector('.pp-popup-close').addEventListener('click', dismiss);
  root.querySelector('.pp-popup-dismiss').addEventListener('click', dismiss);
  root.querySelector('.pp-popup-form').addEventListener('submit', submit);
  root.addEventListener('click', (e) => {
    if (e.target === root) dismiss(); // backdrop click
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('is-open')) dismiss();
  });

  // Trigger setup
  const isMobile = window.matchMedia('(max-width: 640px)').matches;
  if (isMobile) {
    setTimeout(open, MOBILE_TIMER_MS);
  } else {
    // Desktop exit-intent — mouse leaves window from the top
    let triggered = false;
    document.addEventListener('mouseout', (e) => {
      if (triggered) return;
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        triggered = true;
        open();
      }
    });
    // Backup: 60s timer on desktop too (in case mouse never leaves)
    setTimeout(() => { if (!triggered) { triggered = true; open(); } }, 60000);
  }

  // ── Functions ────────────────────────────────────────────

  function loadState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (e) { return {}; }
  }

  function saveState(patch) {
    try {
      const merged = { ...loadState(), ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (e) {}
  }

  function pickVariant() {
    const fromPinterest = (
      /pinterest\.com/i.test(document.referrer || '') ||
      /[?&]utm_source=pinterest/i.test(location.search || '')
    );
    if (fromPinterest) return 'pinterest';
    const path = location.pathname.toLowerCase();
    if (/bach|wedding|brid/i.test(path)) return 'bach';
    return 'default';
  }

  function open() {
    if (state.submitted || state.dismissedAt) return;
    root.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const input = root.querySelector('.pp-popup-input');
    if (input) setTimeout(() => input.focus(), 320);
  }

  function close() {
    root.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function dismiss() {
    saveState({ dismissedAt: Date.now(), dismissedVariant: variant });
    close();
  }

  async function submit(e) {
    e.preventDefault();
    const input = root.querySelector('.pp-popup-input');
    const cta = root.querySelector('.pp-popup-cta');
    const email = input.value.trim();
    if (!email) return;
    cta.disabled = true;
    cta.textContent = 'Saving…';

    try {
      const cart = await fetchCart();
      await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: `popup-${variant}`,
          page: location.pathname,
          cart,
        }),
      });
      saveState({ submitted: true, submittedVariant: variant, submittedAt: Date.now() });
      showSuccess();
      setTimeout(close, 3500);
    } catch (err) {
      cta.disabled = false;
      cta.textContent = 'Try again';
    }
  }

  function showSuccess() {
    const cardEl = root.querySelector('.pp-popup-content');
    cardEl.innerHTML = `
      <div class="pp-popup-success">
        <h2 class="pp-popup-success-headline">oh hi! we're in.</h2>
        <p class="pp-popup-success-sub">Check your inbox — your code is on its way.</p>
        <p class="pp-popup-success-micro">(peek in spam if it's shy. it happens.)</p>
      </div>
    `;
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) { return null; }
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  // ── Iris's variant copy ──────────────────────────────────

  const VARIANTS = {
    default: {
      eyebrow: 'Save your build',
      headline: 'wait — save your build?',
      subhead: "We'll email the link so you can finish later. No pressure.",
      body: "Pop back anytime. Your patches will be right where you left 'em.",
      placeholder: 'email to save your hat',
      cta: 'save my build',
      dismiss: "I'll start over later",
    },
    pinterest: {
      eyebrow: 'From Pinterest? same energy.',
      headline: 'saw us on Pinterest? same.',
      subhead: 'Get the patches that made you stop scrolling — 15% off inside.',
      body: 'Late-90s SoCal energy, delivered to your door (and inbox).',
      placeholder: 'your email, please',
      cta: 'yes send the vibes',
      dismiss: 'just browsing tbh',
    },
    bach: {
      eyebrow: 'For bach planners',
      headline: 'the $85 bach build, unlocked.',
      subhead: 'The exact 6-patch combo bach groups keep reordering.',
      body: "We'll send the build link + 15% off. You'll look like the planner.",
      placeholder: 'drop your email',
      cta: 'send me the build',
      dismiss: 'not bach planning rn',
    },
  };
})();
